import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type CostReport } from "../api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

/** The pipeline's stage names, in words. */
const STAGE_LABEL: Record<string, string> = {
  ingest: "Download", transcribe: "Transcription", translate: "Translation", polish: "Polish", select: "Clip selection", analyse: "Analysis", captions: "Captions",
  metadata: "Titles and descriptions", direct: "Direction", overlays: "Pictures", pictures: "Pictures", overlayPlan: "Picture plans", overlayImage: "Pictures", music: "Music",
  localise: "Translations", length: "Length review", lengthReview: "Length review", cover: "Covers", library: "Picture library", video: "B-roll video", check: "Picture checks",
};
const usd = (n: number) => `$${n.toFixed(2)}`;
const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** The one screen that shows money: what every lecture and the picture library have cost, by stage and by lecture. */
export const Costs: React.FC = () => {
  const [r, setR] = useState<CostReport | null>(null);
  const toast = useToast();
  useEffect(() => { api.costs().then(setR).catch((e) => toast(e.message)); }, []);
  const stages = r ? Object.entries(r.byStage).sort((a, b) => b[1] - a[1]) : [];
  const top = stages[0]?.[1] ?? 0;
  const clips = r ? r.lectures.reduce((a, l) => a + l.clips, 0) : 0;
  const onLectures = r ? r.lectures.reduce((a, l) => a + l.usd, 0) : 0;
  return (
    <div className="main">
      <div className="page-head" style={{ gap: 10 }}>
        <Link to="/settings" className="btn sm quiet" title="Back to the settings"><Icon name="back" size={14} />Settings</Link>
        <div><h1>Spending</h1><div className="sub">What the AI services have been paid for, in US dollars. This is the only page in the studio that shows money.</div></div>
      </div>
      <div className="page col" style={{ gap: 18 }}>
        {r ? (
          <>
            <div className="costs-tiles">
              <div className="card tile"><span className="k">All time</span><span className="v" data-testid="spend-total">{usd(r.usd)}</span><span className="sub">{plural(r.lectures.length, "lecture")} · {plural(clips, "clip")}</span></div>
              <div className="card tile"><span className="k">This month</span><span className="v">{usd(r.month.usd)}</span><span className="sub">{plural(r.month.lectures, "lecture")} added since {day(r.month.from)}</span></div>
              <div className="card tile"><span className="k">Per clip</span><span className="v">{clips ? usd(onLectures / clips) : "–"}</span><span className="sub">lecture spend over every clip cut</span></div>
              <div className="card tile"><span className="k">Picture library</span><span className="v">{usd(r.library.usd)}</span><span className="sub">{plural(r.library.pictures, "picture")}</span></div>
            </div>
            {stages.length ? (
              <section className="card costs-sec">
                <h3>By stage</h3>
                <div className="costs-bars">
                  {stages.map(([k, v]) => <div key={k} className="bar"><span className="n">{STAGE_LABEL[k] ?? k}</span><span className="t"><i style={{ width: `${Math.max(2, (v / top) * 100)}%` }} /></span><span className="a">{usd(v)}</span></div>)}
                </div>
              </section>
            ) : null}
            <section className="card costs-sec">
              <h3>By lecture</h3>
              {r.lectures.length ? (
                <div className="costs-table-wrap">
                  <table className="costs-table">
                    <thead><tr><th>Lecture</th><th>Added</th><th className="num">Clips</th><th className="num">Cost</th><th className="num">Per clip</th></tr></thead>
                    <tbody>
                      {r.lectures.map((l) => {
                        const parts = Object.entries(l.byStage).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => `${STAGE_LABEL[k] ?? k} ${usd(v)}`).join(" · ");
                        return (
                          <tr key={l.videoId}>
                            <td><Link to={`/lectures/${l.videoId}`}>{l.title}</Link><div className="sub">{l.speaker}{parts ? ` · ${parts}` : ""}</div></td>
                            <td className="when">{day(l.ingestedAt)}</td>
                            <td className="num">{l.clips}</td>
                            <td className="num">{usd(l.usd)}</td>
                            <td className="num">{l.clips ? usd(l.usd / l.clips) : "–"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <div className="muted">No lectures yet.</div>}
            </section>
            <section className="card costs-sec">
              <h3>What things cost</h3>
              <ul className="sub costs-ref">
                <li>A lecture of about an hour: usually one to two dollars for transcription, translation, clip selection, captions, titles and direction.</li>
                <li>A picture made by the AI: about four cents. A batch of twelve library pictures: about fifty cents.</li>
                <li>A five-second B-roll video: about thirty cents.</li>
                <li>Picking clips again from the saved moments, re-rendering, and your own uploads cost nothing.</li>
              </ul>
            </section>
          </>
        ) : <div className="muted">Loading…</div>}
      </div>
    </div>
  );
};
